import { useMemo, useState } from "react"
import { dateTimeStampFormat, zeroPadding } from "shared/util"
import { TextMatch } from "shared/text"

import { Stack, FormControl, Typography } from "@mui/material"

import { ErrorInfo } from "components/controls/errorInfo"
import { Loading } from "components/controls/loading"
import { SearchTableBody } from "components/controls/searchTable"
import { OptionSelect } from "components/controls/optionSelect"

import { AuditLogResultWithId, useListAuditLog } from "interface/auditLog"
import { useListGroup } from "interface/user/group"
import { useListUser } from "interface/user"

import { ConfigureListHeader } from "../components/header"

import { AuditLogPreviewDialog } from "features/auditLog/dialog/preview"

import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'

export const AuditLogSearchPage = () => {
  const [userIdList, setUserIdList] = useState<number[]>([])
  const [groupIdList, setGroupIdList] = useState<number[]>([])
  const [from, setFrom] = useState<string | null>(() => {
    const now = new Date()
    return `${now.getFullYear()}-${zeroPadding(now.getMonth() + 1, 2)}-${zeroPadding(now.getDate(), 2)}T00:00:00`
  })
  const [to, setTo] = useState<string | null>(null)
  const [modal, setModal] = useState<{
    mode: 'detail',
    log?: AuditLogResultWithId
  } | null>(null)

  const [searchWord, setSearchWord] = useState<string>('')

  const groupListState = useListGroup()
  const userListState = useListUser()
  const listState = useListAuditLog({ from, to })

  const filterdList = useMemo<AuditLogResultWithId[] | null>(
    () => {
      if (!listState.data) return null

      const match = searchWord ? new TextMatch(searchWord) : null
      let id = 0
      return listState.data.flatMap(
        log => {
          if (match && !match.match(
            log.userName,
            log.operatorName,
            log.searchTitle,
            log.detail,
            ...log.xForwardedFor ?? []
          )) return []
          if (groupIdList.length > 0 && (
            !log.groupId || !groupIdList.includes(log.groupId)
          )) return []
          if (userIdList.length > 0 && (
            !log.userId || !userIdList.includes(log.userId)
          )) return []
          return { ...log, id: id++ }
        }
      )
    },
    [listState.data, searchWord, userIdList, groupIdList]
  )
  return (
    <>
      {groupListState.loading || userListState.loading || listState.loading && <Loading/>}

      <ConfigureListHeader
        searchWord={searchWord}
        setSearchWord={setSearchWord}
        filters={(
          <Stack direction="row" spacing={1}>
            <FormControl sx={{ minWidth: '12em' }}>
              <OptionSelect
                id='userfilter'
                variant="outlined"
                size="small"
                fullWidth
                value={userIdList}
                onChange={(e) => setUserIdList(e.target.value as number[])}
                optionList={(userListState.data ?? []).map(user => ({
                  id: user.userId,
                  label: user.userName
                }))}
                placeHolder="ユーザー"
              />
            </FormControl>

            <FormControl sx={{ minWidth: '12em' }}>
              <OptionSelect
                id='groupfilter'
                variant="outlined"
                size="small"
                fullWidth
                value={groupIdList}
                onChange={(e) => setGroupIdList(e.target.value as number[])}
                optionList={(groupListState.data?.list ?? []).map(group => ({
                  id: group.groupId,
                  label: group.groupName
                }))}
                placeHolder="グループ"
              />
            </FormControl>
          </Stack>
        )}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="ja"
          >
            <DesktopDatePicker
              format="YYYY/MM/DD HH:mm"
              value={dayjs(from)}
              maxDate={dayjs(new Date())}
              onChange={(value) => setFrom(value?.format('YYYY-MM-DDTHH:mm:ss') ?? null)}
              sx={{
                '& .MuiPickersSectionList-root': {
                  width: 'auto'
                }
              }}
              slotProps={{
                textField: {
                  size: "small",
                  variant: "outlined"
                }
              }}
            />
            <Typography variant="caption">～</Typography>
            <DesktopDatePicker
              format="YYYY/MM/DD HH:mm"
              value={dayjs(to)}
              maxDate={dayjs(new Date())}
              onChange={(value) => setTo(
                (value && (!to || !value.isSame(dayjs(to), 'day'))) ? 
                  (value.format('YYYY-MM-DD') + 'T23:59:59') :
                  (value?.format('YYYY-MM-DDTHH:mm:ss') ?? null)
              )}
              sx={{
                '& .MuiPickersSectionList-root': {
                  width: 'auto'
                }
              }}
              slotProps={{
                textField: {
                  size: "small",
                  variant: "outlined"
                }
              }}
            />

          </LocalizationProvider>
        </Stack>
      </ConfigureListHeader>

      <Stack direction="column" spacing={1}>
        {listState.error && <ErrorInfo error={listState.error}/>}
      </Stack>

      <Stack flexGrow={1} overflow="auto">
        {filterdList && (
          <SearchTableBody<AuditLogResultWithId>
            list={filterdList}
            columnList={[
              { label: '操作者', value: r => r.operatorName },
              { label: '日時', value: r => dateTimeStampFormat(r.logTime) },
              { label: '操作', value: r => r.actionName },
              { label: '分析', value: r => r.searchTitle },
              { label: '詳細', value: r => r.detail, expand: true },
              { label: 'IP', value: r => r.clientIp }
            ]}
            onClickRow={(log) => setModal({ mode: 'detail', log })}
          />
        )}
      </Stack>

      {modal?.mode === 'detail' && modal.log && (
        <AuditLogPreviewDialog
          list={filterdList ?? []}
          log={modal.log}
          onClose={() => setModal(null)}
        />
      )}
    </>
  )
}
